import { useState, useEffect, type ChangeEvent } from 'react';
import { useFormContext, useFieldArray } from 'react-hook-form';
import { getCompagnys } from "@/lib/helpers";
import Text from "@/components/forms/text";
import RadioGroup from "@/components/forms/radio-group";
import PostalCodeCity from "@/components/forms/postal-code-city";
import FilteredAutocomplete from "@/components/shared/filtered-autocomplete";
import { codeCompagnys } from "@/lib/options/select-options";
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Users } from 'lucide-react';

interface Compagny {
    id: string;
    codegta?: string;
    name?: string;
    adresse?: string;
    codePostal?: string;
    ville?: string;
}

interface CompagnyOption {
    value: string;
    codegta?: string;
    label: string;
}

const Adversaires = () => {
    const { control, watch, setValue } = useFormContext();
    const { fields, append } = useFieldArray({ control, name: 'adversaires' });
    const [compagnys, setCompagnys] = useState<CompagnyOption[]>([]);

    const nbVehicules = watch('nb_vehicules');

    useEffect(() => {
        (async function fetchCompagnys() {
            const compagnys = await getCompagnys<Compagny[]>();
            const listcompanys = compagnys.map(company => ({
                value: company.id,
                codegta: company.codegta,
                label: company.name + ' - ' + company.adresse + ' - ' + company.codePostal + ' ' + company.ville
            }));
            setCompagnys(listcompanys);
        })();
    }, []);

    useEffect(() => {
        if (Number(nbVehicules) > 1) {
            append({});
        }
    }, [nbVehicules]);

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setValue(name, value);
    };

    return (
        <Card className="mt-6">
            <CardHeader className="pb-6 border-b">
                <CardTitle className="text-lg flex items-center gap-2">
                    <Users className="w-5 h-5 text-primary" />
                    Adversaire(s)
                </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
                <div className="space-y-6">
                    {fields.map((field, i) => (
                        <div key={field.id} className="space-y-8">
                            <p className="text-base font-semibold">Partie adverse n°{i + 1}</p>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                <Text name={`adversaires.${i}.immat`} onChange={handleChange} label="Immatriculation" value={watch(`adversaires.${i}.immat`)} normalize={v => v.toUpperCase()} />
                                <Text name={`adversaires.${i}.police`} onChange={handleChange} label="Numéro de contrat d'assurance" value={watch(`adversaires.${i}.police`)} normalize={v => v.toUpperCase()} />
                            </div>

                            <FilteredAutocomplete
                                name={`adversaires.${i}.cie`}
                                label="Compagnie d'assurance"
                                labelothervalue="Autre compagnie"
                                data={compagnys}
                                datafiltered={compagnys.filter(c => codeCompagnys.includes(c.value))}
                                setValue={setValue}
                                onChange={(value) => setValue('codegta', (compagnys.find((c) => c.value === value) as CompagnyOption).codegta)}
                            />

                            <RadioGroup
                                label="Civilité"
                                name={`adversaires.${i}.titre`}
                                setValue={setValue}
                                options={[{ value: "M", label: "Monsieur" }, { value: "Mme", label: "Madame" }, { value: "Societe", label: "Société" }]}
                                value={watch(`adversaires.${i}.titre`)}
                                onChange={(e) => {
                                    if (e.target.value === 'Societe') {
                                        setValue(`adversaires.${i}.prenom`, '');
                                    }
                                }}
                            />

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                <Text name={`adversaires.${i}.nom`} onChange={handleChange} label="Nom" value={watch(`adversaires.${i}.nom`)} normalize={v => v.toUpperCase()} />
                                {watch(`adversaires.${i}.titre`) !== 'Societe' && (
                                    <Text name={`adversaires.${i}.prenom`} onChange={handleChange} label="Prénom" value={watch(`adversaires.${i}.prenom`)} normalize={v => v.toUpperCase()} />
                                )}
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                <RadioGroup label="Y-a-t-il des blessés dans cette partie adverse ?" name={`adversaires.${i}.blesse`} setValue={setValue} options={[{ value: "T", label: "Oui" }, { value: "F", label: "Non" }]} value={watch(`adversaires.${i}.blesse`)} />
                                <RadioGroup label="Avez-vous son adresse ?" name={`adversaires.${i}.adresse`} setValue={setValue} options={[{ value: "T", label: "Oui" }, { value: "F", label: "Non" }]} value={watch(`adversaires.${i}.adresse`)} />
                            </div>

                            {watch(`adversaires.${i}.adresse`) === 'T' && (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                    <Text name={`adversaires.${i}.adresse1`} onChange={handleChange} label="Adresse" value={watch(`adversaires.${i}.adresse1`)} normalize={v => v.toUpperCase()} />
                                    <PostalCodeCity label="Ville de résidence" cp={`adversaires.${i}.cp`} ville={`adversaires.${i}.ville`} />
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </CardContent>
        </Card>
    );
};

export default Adversaires;
